import { defineStore } from 'pinia';
import { SearchAvailabilityRequestDTO } from 'src/services/api/data-contracts';
import { useNewBookingStore } from 'src/stores/newBookingStore';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { useServiceStore } from 'src/stores/serviceStore';
import { IRoom, ProcessStep, RoomTypeAvailability } from 'src/types';
import useRateHelperUtils from 'src/composables/rateHelperUtils';
import useUtils from 'src/composables/utils';
import { i18n } from 'boot/i18n';

const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();
const t = i18n.global.t;

const apiService = Services.getService(Api);

export const useSearchAvailabilityStore = defineStore(
  'searchAvailabilityStore',
  {
    state: () => ({
      isSearching: false as boolean,
      searchHasBeenPerformed: false as boolean,
      isPromoCodeValid: true as boolean,
      promoCodeMessage: '' as string,
      isHopGiftVoucherCodeValid: true as boolean,
      hopGiftVoucherCodeMessage: '' as string,
      restrictedBookingWarning: '' as string,

      RoomTypes: [] as RoomTypeAvailability[],

      availableRoomsPerRoomTypeId: {} as Record<number, number>,
    }),
    getters: {
      availableRoomTypesForActiveRoomSelection(): Array<RoomTypeAvailability> {
        const newBookingStore = useNewBookingStore();
        const activeReservationItem = newBookingStore.activeReservationItem;
        const activeRoom = newBookingStore.activeRoom;

        const allRoomTypes = this.RoomTypes ?? [];

        const roomId = activeReservationItem?.roomId ?? activeRoom?.roomId;

        const roomData: IRoom = activeReservationItem ?? activeRoom;

        if (roomData != null) {
          const resX = newBookingStore.getReservationItem(roomId);

          const availableRoomTypes = allRoomTypes.filter(
            (roomType: RoomTypeAvailability) => {
              const rates = roomType.Rates;

              const validRatePlans = rates.filter((u) => {
                let ratePlanOccupancyIsValid = true;
                if (u.UseOccupancyForRatePlan) {
                  ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
                    roomData.numberOfAdults,
                    roomData.numberOfChildren,
                    roomData.numberOfInfants,
                    utils.getSmallestMaximumValue(
                      u.MaximumOccupancy,
                      roomType.RoomType.MaximumOccupancy,
                    ),
                    utils.getLargestMinimumValue(
                      u.MinimumOccupancy,
                      roomType.RoomType.MinimumOccupancy,
                    ),
                    utils.getSmallestMaximumValue(
                      u.AdultMaximumOccupancy,
                      roomType.RoomType.AdultMaximumOccupancy,
                    ),
                    utils.getSmallestMaximumValue(
                      u.ChildMaximumOccupancy,
                      roomType.RoomType.ChildMaximumOccupancy,
                    ),
                  );
                }

                return ratePlanOccupancyIsValid;
              });

              // Recalculate the lowest rate
              roomType.LowestRate = rateHelperUtils.calculateLowestRoomRate(
                roomData,
                validRatePlans,
                false,
              );
              roomType.OriginalLowestRate =
                rateHelperUtils.calculateLowestRoomRate(
                  roomData,
                  validRatePlans,
                  true,
                );

              if (
                resX != null &&
                resX.roomTypeId == roomType.RoomType.RoomTypeId
              ) {
                return true;
              }

              const roomTypeMatch =
                newBookingStore.roomTypeId == null ||
                (newBookingStore.roomTypeId != null &&
                  newBookingStore.roomTypeId == roomType.RoomType.RoomTypeId);

              const roomCount =
                this.availableRoomsPerRoomTypeId[roomType.RoomType.RoomTypeId];
              const hasRoomsAvailable = roomCount != null && roomCount > 0;

              const roomTypeOccupancyIsValid = utils.checkIfOccupancyIsValid(
                activeRoom.numberOfAdults,
                activeRoom.numberOfChildren,
                activeRoom.numberOfInfants,
                roomType.RoomType.MaximumOccupancy,
                roomType.RoomType.MinimumOccupancy,
                roomType.RoomType.AdultMaximumOccupancy,
                roomType.RoomType.ChildMaximumOccupancy,
              );

              const ratePlanOccupanciesAreValid = validRatePlans.length > 0;

              return (
                roomTypeMatch &&
                hasRoomsAvailable &&
                roomTypeOccupancyIsValid &&
                ratePlanOccupanciesAreValid
              );
            },
          );

          return availableRoomTypes;
        }
        return [];
      },

      totalAvailableRooms(): number {
        let total = 0;
        for (const key in this.availableRoomsPerRoomTypeId) {
          if (this.availableRoomsPerRoomTypeId.hasOwnProperty(key)) {
            const availableRooms = this.availableRoomsPerRoomTypeId[key];

            total = total + availableRooms;
          }
        }
        return total;
      },

      totalLowestRate(): number {
        const newBookingStore = useNewBookingStore();
        const total = rateHelperUtils.calculateTotalRoomRate(
          newBookingStore.rooms,
          this.RoomTypes,
          false,
        );
        return total;
      },
      totalOriginalLowestRate(): number {
        const newBookingStore = useNewBookingStore();
        const total = rateHelperUtils.calculateTotalRoomRate(
          newBookingStore.rooms,
          this.RoomTypes,
          true,
        );
        return total;
      },
      hasResults(): boolean {
        return this.RoomTypes.length > 0;
      },
      hasRoomTypesForActiveRoomSelection(): boolean {
        return this.availableRoomTypesForActiveRoomSelection.length > 0;
      },
      showResults(): boolean {
        return this.hasResults && this.isSearching == false;
      },
      showPromoCodeWarning(): boolean {
        return (
          !this.isSearching &&
          !this.isPromoCodeValid &&
          this.promoCodeMessage != '' &&
          this.promoCodeMessage != null
        );
      },
      showNoRoomsAvailableForOccupancyWarning(): boolean {
        return (
          !this.isSearching &&
          this.hasResults &&
          !this.hasRoomTypesForActiveRoomSelection
        );
      },
      showNoRoomsAvailableWarning(): boolean {
        return (
          (!this.isSearching &&
            !this.hasResults &&
            this.promoCodeMessage == '' &&
            this.noRoomsAvailableMessage != '') ||
          !this.hasRoomTypesForActiveRoomSelection
        );
      },
      noRoomsAvailableMessage(): string {
        const nb = useNewBookingStore();
        let noAvailabilityMessage = nb.noAvailabilityMessage;
        if (!this.hasRoomTypesForActiveRoomSelection) {
          noAvailabilityMessage = t(
            'roomsAndRates.notEnoughRoomsBasedOnOccupancy',
          );
        }
        return noAvailabilityMessage;
      },
      showVoucherCodeMessage(): boolean {
        return this.hopGiftVoucherCodeMessage && this.isHopGiftVoucherCodeValid;
      },
    },
    actions: {
      initializeAvailableRoomsPerRoomType(
        numberOfRoomsPerRoomTypeId: Record<number, number>,
      ) {
        this.availableRoomsPerRoomTypeId = {};

        for (const key in numberOfRoomsPerRoomTypeId) {
          if (numberOfRoomsPerRoomTypeId.hasOwnProperty(key)) {
            const roomTypeId = key;
            const availableRooms = numberOfRoomsPerRoomTypeId[key];

            let obs = this.availableRoomsPerRoomTypeId[roomTypeId];
            if (!obs) {
              obs = availableRooms;
              this.availableRoomsPerRoomTypeId[roomTypeId] = obs;
            } else {
              obs = availableRooms;
            }
          }
        }
      },
      decreaseAvailabilityByOne(roomTypeId: number) {
        const a = this.getAvailability(roomTypeId);
        this.availableRoomsPerRoomTypeId[roomTypeId] = a - 1;
      },
      increaseAvailabilityByOne(roomTypeId: number) {
        const a = this.getAvailability(roomTypeId);
        this.availableRoomsPerRoomTypeId[roomTypeId] = a + 1;
      },
      updateAvailability(roomTypeId: number, newAvailability: number) {
        this.availableRoomsPerRoomTypeId[roomTypeId] = newAvailability;
      },
      getAvailability(roomTypeId: number): number | null {
        const obs = this.availableRoomsPerRoomTypeId[roomTypeId];
        return obs ? obs : null;
      },
      showAvailabilityBadge(roomTypeId: number): boolean {
        const obs = this.availableRoomsPerRoomTypeId[roomTypeId];
        return obs ? obs <= 3 : false;
      },

      async search() {
        this.isSearching = true;
        const newBookingStore = useNewBookingStore();

        newBookingStore.setRooms(newBookingStore.searchControlRooms);

        newBookingStore.resetActiveRoom();
        newBookingStore.resetActiveReservationItem();
        newBookingStore.resetBasket();

        const serviceStore = useServiceStore();
        serviceStore.clearCache();

        const dto: SearchAvailabilityRequestDTO = {
          PropertyId: newBookingStore.propertyId,
          LanguageCode: newBookingStore.languageCode,
          PromoCode: newBookingStore.promoCode,
          RatePlanCode: newBookingStore.ratePlanCode,
          DateFrom: newBookingStore.fromDate,
          DateTo: newBookingStore.toDate,
          LocationId: newBookingStore.location?.LocationId,
          Rooms: newBookingStore.rooms.map((r) => {
            const g = {
              NumberOfAdults: r.numberOfAdults,
              NumberOfChildren: r.numberOfChildren,
              NumberOfInfants: r.numberOfInfants,
            };
            return g;
          }),
        };

        const searchDataResponse = await apiService.availabilitySearch(dto);

        this.RoomTypes = searchDataResponse.data.RoomTypes.map(
          (d) => new RoomTypeAvailability(d),
        );

        this.promoCodeMessage = searchDataResponse.data.PromoCodeMessage;
        this.isPromoCodeValid = searchDataResponse.data.IsPromoCodeValid;

        this.hopGiftVoucherCodeMessage =
          searchDataResponse.data.HopGiftVoucherCodeMessage;
        this.isHopGiftVoucherCodeValid =
          searchDataResponse.data.IsHopGiftVoucherCodeValid;

        this.initializeAvailableRoomsPerRoomType(
          searchDataResponse.data.NumberOfRoomsPerRoomTypeId,
        );

        newBookingStore.processStep = ProcessStep.RoomTypeSelection;
        newBookingStore.next();

        this.searchHasBeenPerformed = true;
        this.isSearching = false;
      },
    },
  },
);
